import { ReactNode } from 'react';

import styled from 'styled-components';

import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { editableCellLocale } from './locale';

import { isEmptyColumnValue } from '../table-utils';
import { AvailableColumn, DataItemType } from '../types';

const TriggerContent = styled.div({
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

// Every editor's trigger is either the empty-value fallback (renderFallback,
// falling back to the shared "Not set" text) or a type-specific render of the
// current value — this hook owns that branch so it isn't repeated per editor.
// The wrapper keeps a long value from blowing up the row height: it forces a
// single-line ellipsis instead of wrapping or growing the flex row.
// renderEmpty is for a type whose designs draw their own empty state (a file
// column's "No file"), and still yields to a column's own renderFallback.
export const useEditableCellTrigger = () => {
  const { t } = useScopedTranslate(editableCellLocale);

  return (
    value: any,
    column: AvailableColumn<any>,
    row: DataItemType,
    renderValue: () => ReactNode,
    renderEmpty?: () => ReactNode
  ): ReactNode => (
    <TriggerContent>
      {isEmptyColumnValue(value)
        ? column.renderFallback?.(row) ??
          renderEmpty?.() ?? <Text text={t('core.not_set')} color="textHelper" font="regular14" />
        : renderValue()}
    </TriggerContent>
  );
};
